import React, { useRef, useEffect, memo } from 'react';
import PdfRenderer from './PdfRenderer/PdfRenderer';
import HighlightLayer from './HighlightSystem/HighlightLayer';
import CommentLayer from './CommentSystem/CommentLayer';
import HoverSelectionWindow from './ui/HoverSelectionWindow';
import SelectionSendButton from './SelectionSendButton';

function PdfContentArea({
  filePath,
  pageNumber,
  zoom,
  deviceScale,
  onDocumentLoadSuccess,
  pdfScrollContainerRef,
  highlights,
  comments,
  activeComment,
  tempComment,
  onCommentClick,
  onCommentMove,
  onSaveComment,
  onDeleteComment,
  onCancelComment,
  selectedText,
  selectionRects,
  selectionContext,
  hasHighlightOnSelection,
  matchingHighlightIds,
  onSendToChat,
  onAddHighlight,
  onRemoveMultipleHighlights,
  onPdfNewlyRendered,
  commentMode,
  onDeleteMultipleHighlights,
  onDeleteHighlight,
  onBackToDashboard // Add this prop
}) {
  const pdfViewerContainerRef = useRef(null);

  // Apply zoom dynamically to the container
  useEffect(() => {
    if (pdfViewerContainerRef.current) {
      pdfViewerContainerRef.current.style.setProperty('--zoom-level', zoom);
    }
  }, [zoom]);

  // Handle removing multiple highlights
  const handleRemoveMultipleHighlights = () => {
    if (!matchingHighlightIds || matchingHighlightIds.length === 0) {
      console.log('⚠️ No highlights to remove');
      return;
    }

    console.log('🗑️ Removing multiple highlights from PdfContentArea:', matchingHighlightIds);
    
    // Use the bulk delete function if available
    if (onDeleteMultipleHighlights) {
      onDeleteMultipleHighlights(matchingHighlightIds);
    } 
    // Otherwise use the single delete function multiple times
    else if (onDeleteHighlight) {
      matchingHighlightIds.forEach(id => {
        onDeleteHighlight(id);
      });
      console.log(`✅ Removed ${matchingHighlightIds.length} highlights`);
    }
    
    // Clear selection after removal
    window.getSelection()?.removeAllRanges();
  };

  return (
    <div className="pdf-viewer-container">
      {/* Floating Back Button - Single button hovering on top */}
      {onBackToDashboard && (
        <div className="pdf-back-button-container">
          <button 
            className="pdf-back-button"
            onClick={onBackToDashboard}
            aria-label="Back to Dashboard"
            title="Back to Dashboard"
          >
            🔙
          </button>
        </div>
      )}

      {/* Scroll Container */}
      <div className="pdf-scroll-container" ref={pdfScrollContainerRef}>
        <div id="pdf-viewer-wrapper">
          {/* PDF Container with zoom applied */}
          <div 
            id="pdf-viewer-container" 
            className="pdf-renderer-wrapper"
            ref={pdfViewerContainerRef}
            style={{ '--zoom-level': zoom }}
          >
            <PdfRenderer
              filePath={filePath}
              pageNumber={pageNumber}
              zoom={zoom}
              deviceScale={deviceScale}
              onDocumentLoadSuccess={onDocumentLoadSuccess}
              onPdfNewlyRendered={onPdfNewlyRendered}
            >
              <HighlightLayer 
                highlights={highlights} 
                pageNumber={pageNumber} 
                zoom={zoom} 
                key={`highlights-${filePath}-${pageNumber}`}
              />
              <CommentLayer 
                comments={comments}
                pageNumber={pageNumber}
                zoom={zoom}
                activeComment={activeComment}
                tempComment={tempComment}
                onCommentClick={onCommentClick}
                onCommentMove={onCommentMove}
                onSaveComment={onSaveComment}
                onDeleteComment={onDeleteComment}
                onCancelComment={onCancelComment}
                containerRef={pdfViewerContainerRef} // Pass the container ref here
                key={`comments-${filePath}-${pageNumber}`}
              />
            </PdfRenderer>
          </div>
        </div>
      </div>

      {/* Selection Send Button */}
      <SelectionSendButton
        selectionRects={selectionRects}
        selectedText={selectedText}
        selectionContext={selectionContext}
        hasHighlightOnSelection={hasHighlightOnSelection}
        matchingHighlightIds={matchingHighlightIds}
        onSend={onSendToChat}
        onAddHighlight={onAddHighlight}
        onRemoveHighlight={onDeleteHighlight}
        onRemoveMultipleHighlights={handleRemoveMultipleHighlights}
        zoom={zoom}
      />

      <HoverSelectionWindow text={selectedText} />
    </div>
  );
}

// Export memoized version
export default memo(PdfContentArea, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.filePath === nextProps.filePath &&
    prevProps.pageNumber === nextProps.pageNumber &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.deviceScale === nextProps.deviceScale &&
    prevProps.highlights === nextProps.highlights &&
    prevProps.comments === nextProps.comments &&
    prevProps.activeComment === nextProps.activeComment &&
    prevProps.tempComment === nextProps.tempComment &&
    prevProps.selectedText === nextProps.selectedText &&
    prevProps.selectionRects === nextProps.selectionRects &&
    prevProps.hasHighlightOnSelection === nextProps.hasHighlightOnSelection &&
    prevProps.matchingHighlightIds === nextProps.matchingHighlightIds &&
    prevProps.commentMode === nextProps.commentMode &&
    prevProps.onBackToDashboard === nextProps.onBackToDashboard
  );
});